import { inject, Injectable } from '@angular/core';
import { ApolloQueryResult } from '@apollo/client/core/types';
import { ApolloService } from '@services/apollo/apollo.service';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { getMinimumWagesQuery } from '../graphql/get-minimum-wages.query';
import {
  DeleteMinimumWageResponse,
  ManageMinimumWageVars,
} from '../types/delete-minimum-wage.type';
import { minimumWagesMutations } from '../graphql/create-minimum-wage-mutations';
import { deleteMinimumWageMutation } from '../graphql/delete-minimum-wage-mutation';
import { editMinimumWageMutation } from '../graphql/edit-minimum-wage-mutation';
import { GetMinimumWagesQueryResponse } from '../types/get-minimum-wages-query-response.type';
import { WageDataQueryMinimumWagesArgs } from 'src/app/core/services/wage/wage.types';
import { MinimumWageScopeConfig } from '../manage-minimum-wage-form/enum/minimum-wage-scope.enum';
import {
  MinimumWageMutationResponse,
  minimumWageResponse,
  CreateOrUpdateMutationMinimumWageArgs,
} from '../manage-minimum-wage-form/interfaces/minimum-wage-form.interface';

@Injectable({
  providedIn: 'root',
})
export class ManageMinimumWageService {
  private readonly _apolloService = inject(ApolloService);
  private readonly _serverName: string = environment.graphql.wagedata.name;

  getMinimumWages(
    vars: WageDataQueryMinimumWagesArgs
  ): Observable<ApolloQueryResult<GetMinimumWagesQueryResponse>> {
    return this._apolloService.query(
      this._serverName,
      getMinimumWagesQuery,
      vars
    );
  }

  deleteMinimumWages(
    vars: ManageMinimumWageVars
  ): Observable<DeleteMinimumWageResponse> {
    return this._apolloService
      .mutateOperation<DeleteMinimumWageResponse>(
        this._serverName,
        deleteMinimumWageMutation,
        vars
      )
      .pipe(map(response => response.data!));
  }

  createMinimumWage(
    vars: CreateOrUpdateMutationMinimumWageArgs,
    type: MinimumWageScopeConfig
  ): Observable<minimumWageResponse> {
    // Determine which mutation to call based on the scope type
    switch (minimumWagesMutations[type]) {
      // All Projects In Jurisdiction
      case minimumWagesMutations['All Locations In Project']:
        return this.createMinimumWageAllLocationsInProject(vars);
      // All Locations In Project
      case minimumWagesMutations['Specific Location In Project']:
        return this.createMinimumWageSpecificLocationInProject(vars);
      // Specific Location In Project
      default:
        return this.createMinimumWageAllProjectsAndLocationsInJurisdiction(
          vars
        );
    }
  }

  createMinimumWageAllProjectsAndLocationsInJurisdiction(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        minimumWagesMutations['All Projects In Jurisdiction'],
        vars
      )
      .pipe(
        map(({ data }) => data!.createMinimumWageForAllProjectsInJurisdiction)
      );
  }

  createMinimumWageAllLocationsInProject(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        minimumWagesMutations['All Locations In Project'],
        vars
      )
      .pipe(map(({ data }) => data!.createMinimumWageForAllLocationsInProject));
  }

  createMinimumWageSpecificLocationInProject(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        minimumWagesMutations['Specific Location In Project'],
        vars
      )
      .pipe(
        map(({ data }) => data!.createMinimumWageForSpecificLocationInProject)
      );
  }

  updateMinimumWage(
    vars: CreateOrUpdateMutationMinimumWageArgs,
    type: MinimumWageScopeConfig
  ): Observable<minimumWageResponse> {
    // Determine which mutation to call based on the scope type
    switch (minimumWagesMutations[type]) {
      // All Projects In Jurisdiction
      case minimumWagesMutations['All Locations In Project']:
        return this.updateMinimumWageAllLocationsInProject(vars);
      // All Locations In Project
      case minimumWagesMutations['Specific Location In Project']:
        return this.updateMinimumWageSpecificLocationInProject(vars);
      // Specific Location In Project
      default:
        return this.updateMinimumWageAllLocationsAndProjectsInJurisdiction(
          vars
        );
    }
  }

  updateMinimumWageAllLocationsAndProjectsInJurisdiction(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        editMinimumWageMutation['All Projects In Jurisdiction'],
        vars
      )
      .pipe(
        map(({ data }) => data!.updateMinimumWageForAllProjectsInJurisdiction)
      );
  }

  updateMinimumWageAllLocationsInProject(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        editMinimumWageMutation['All Locations In Project'],
        vars
      )
      .pipe(map(({ data }) => data!.updateMinimumWageForAllLocationsInProject));
  }

  updateMinimumWageSpecificLocationInProject(
    vars: CreateOrUpdateMutationMinimumWageArgs
  ): Observable<minimumWageResponse> {
    return this._apolloService
      .mutateOperation<MinimumWageMutationResponse>(
        this._serverName,
        editMinimumWageMutation['Specific Location In Project'],
        vars
      )
      .pipe(
        map(({ data }) => data!.updateMinimumWageForSpecificLocationInProject)
      );
  }
}
